import { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { RoutesList } from 'routes';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { doLogout } from 'store/slices/auth';
import { selectPartnerSettingsSubscription } from 'store/slices/partnerSettings';
import {
    setUserDataLanguage,
    selectUserData,
    setUserDataApplicationModes,
    selectUserApplicationModes,
    setUserDataFetch,
    setUserDataFailed,
} from 'store/slices/userData';
import {
    getHeaderPartnersFetch,
    selectHeaderNavigation,
    getHeaderPartnersSucceeded,
    getHeaderPartnersFailed,
} from 'store/slices/header';
import {
    getSubscriptionPlansFailed,
    getSubscriptionPlansFetch,
    getSubscriptionPlansSucceeded,
} from 'store/slices/subscriptionPlans';
// Import Components
import Navigation from './Navigation';
import Create from './Create';
import Partners from './Partners';
import MaintenanceInfo from './MaintenanceInfo';
import UserTime from './UserTime';
import SubscriptionInfoModal from './SubscriptionInfoModal';
import GlobalSearch from 'components/GlobalSearch';
import UserProfile from './UserProfile';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import NotificationPanel from 'components/common/NotificationsPanel/NotificationPanel';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
import ScrollingText from 'components/common/ScrollingText';
// Import UI Components
import { Button, Popover, Image, SkeletonLoader, Divider, Icon } from '@geneui/components';
// Import Services
import { AuthHttpService, UtilsHttpService } from 'services/http';
import { publish } from 'services/event';
// Import Hooks
import { useLocalization } from 'hooks/useLocalization';
import { useGTMEvent } from 'hooks/UseGTM';
import useGoToRoute from 'hooks/GoToRoute';
import useOnClickOutside from 'hooks/UseOnClickOutside';
import useMaintenanceSettings from 'hooks/UseMaintenanceSettings';
import useHasPermission from 'hooks/UseHasPermission';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useDesktopNotifications } from 'hooks/UseDesktopNotifications';
// Import Constants
import { NavigationList } from './constants';
import { l, ViewThemeTypes, ViewModeTypes, GlobalEvents, GACategoryEvents, AuthProvidersTypes } from 'constants/common';
import { BrowserStorageKeys } from 'constants/browserStorage';
// Import Enums
import { PermissionEnum, SubscriptionSectionEnum } from 'enums';
// Import Styles
import 'assets/scss/header.scss';
import styles from './header.module.scss';

const { GACreateCategory, GAPredefinedCategory } = GACategoryEvents;
const { PREDEFINED_LIST, HOMEPAGE } = RoutesList;

interface Props {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
}

const Header: React.FC<Props> = ({ isLoading, setIsLoading }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { goToRoute, getRoutePath } = useGoToRoute();
    const [setLocalization] = useLocalization();
    const { hasPermission, hasSubscriptionSection } = useHasPermission();
    const { doGetRequest } = useRequest();
    const { startDate, endDate, hasUpcomingMaintenance, isUnderMaintenance } = useMaintenanceSettings();
    const isNotificationsAvailable = hasSubscriptionSection(SubscriptionSectionEnum.Notification, false);
    const { requestPermission } = useDesktopNotifications();

    const isPredefinedAvailable = hasSubscriptionSection(SubscriptionSectionEnum.Predefined);

    const applicationModes = useAppSelector(selectUserApplicationModes);
    const userData = useAppSelector(selectUserData);
    const { FirstName, UserSession } = userData ?? {};
    const { displayNameKey: activeNavigationName, route: path } = useAppSelector(selectHeaderNavigation);
    const { DisplayName } = useAppSelector(selectPartnerSettingsSubscription) ?? {};
    const { logEvent } = useGTMEvent();

    const { getSubscriptionPlansRequest } = useCancelableRequests(
        { getSubscriptionPlansRequest: UtilsHttpService.getSubscriptionPlans },
        { takeLatest: ['getSubscriptionPlansRequest'] },
    );
    const createRef = useRef(null);
    const productsRef = useRef(null);
    const navigationRef = useRef(null);
    const userProfileRef = useRef(null);

    const [navigationVisibilityStatus, setNavigationVisibilityStatus] = useState(false);
    const [createVisibilityStatus, setCreateVisibilityStatus] = useState(false);
    const [partnersVisibilityStatus, setPartnersVisibilityStatus] = useState(false);
    const [productsVisibilityStatus, setProductsVisibilityStatus] = useState(false);
    const [userProfileVisibilityStatus, setUserProfileVisibilityStatus] = useState(false);
    const [isVisibleUserProfileSettingsModal, setIsVisibleUserProfileSettingsModal] = useState(false);
    const [isVisibleProfileModal, setIsVisibleProfileModal] = useState(false);

    const [isSubscriptionInfoModalOpen, setIsSubscriptionInfoModalOpen] = useState(false);

    const signOutHandler = () => {
        AuthHttpService.logout().then((response: AnyObject) => {
            if (!response.HasError) {
                if (UserSession?.ProviderId === AuthProvidersTypes.Password) {
                    localStorage.setItem(BrowserStorageKeys.isCRMSignIn, 'true');
                } else {
                    localStorage.removeItem(BrowserStorageKeys.isCRMSignIn);
                }

                localStorage.removeItem(BrowserStorageKeys.accessToken);
                dispatch(doLogout());
            }
        });
    };

    const changeLanguageHandler = (langId: string) => {
        AuthHttpService.saveLanguageSetting(langId)
            .then((response: AnyObject) => {
                const { HasError, AlertMessage } = response;
                if (!HasError) {
                    setLocalization(langId);
                    dispatch(setUserDataLanguage(langId));
                    publish(GlobalEvents.LanguageChanged);
                } else {
                    // eslint-disable-next-line no-console
                    console.log(AlertMessage);
                }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log(error);
            });
    };

    const saveSettings = (settings, isOnlyModeChanged) => {
        dispatch(setUserDataFetch());
        if (!isOnlyModeChanged) setIsLoading(true);

        return AuthHttpService.updateSettings(settings)
            .then((response: AnyObject) => {
                const { HasError, AlertMessage } = response;

                if (!HasError) {
                    if (isOnlyModeChanged) {
                        const { ViewMode, Appearance } = settings;
                        dispatch(setUserDataApplicationModes({ ViewMode, Appearance }));
                    } else {
                        goToRoute({ path });
                    }
                    setUserProfileVisibilityStatus(false);
                } else {
                    dispatch(setUserDataFailed(AlertMessage));
                }
                publish(GlobalEvents.UserSettingsChange, settings);
            })
            .catch((error) => {
                dispatch(setUserDataFailed(error));
                if (!isOnlyModeChanged) setIsLoading(false);
            });
    };

    useOnClickOutside(
        createRef,
        useCallback(() => {
            if (createVisibilityStatus) {
                setCreateVisibilityStatus(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [createVisibilityStatus]),
    );

    useOnClickOutside(
        productsRef,
        useCallback(() => {
            if (productsVisibilityStatus) {
                setProductsVisibilityStatus(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [productsVisibilityStatus]),
    );

    useOnClickOutside(
        userProfileRef,
        useCallback(() => {
            if (userProfileVisibilityStatus) {
                setUserProfileVisibilityStatus(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [userProfileVisibilityStatus]),
        isVisibleProfileModal || isVisibleUserProfileSettingsModal,
    );

    useOnClickOutside(
        navigationRef,
        useCallback(() => {
            if (navigationVisibilityStatus) {
                setNavigationVisibilityStatus(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [navigationVisibilityStatus]),
    );

    // stable reference so that memo(Partners) does not re-render on every Header render
    const togglePartnersVisibility = useCallback(() => setPartnersVisibilityStatus((isVisible) => !isVisible), []);

    const getPartnersSettings = () => {
        return AuthHttpService.getPartners().then(({ Data, HasError, Message }) => {
            if (HasError) {
                throw new Error(Message);
            }

            return Data;
        });
    };

    const getCurrenciesSettings = () => {
        return AuthHttpService.getCurrencies().then(({ Data, HasError, Message }) => {
            if (HasError) {
                throw new Error(Message);
            }

            return Data;
        });
    };

    const getSubscriptionPlans = () => {
        dispatch(getSubscriptionPlansFetch());
        doGetRequest(getSubscriptionPlansRequest.request, {
            successCallback: (data) => {
                dispatch(getSubscriptionPlansSucceeded(data));
            },
            errorCallback: () => {
                dispatch(getSubscriptionPlansFailed());
            },
        });
    };

    const init = () => {
        dispatch(getHeaderPartnersFetch());
        Promise.all([getPartnersSettings(), getCurrenciesSettings()])
            .then(([partners, currencies]) => {
                dispatch(getHeaderPartnersSucceeded({ partners, currencies }));
            })
            .catch(() => dispatch(getHeaderPartnersFailed()));
        requestPermission();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getSubscriptionPlans, [t]);

    const handleCreate = () => {
        logEvent(
            GACreateCategory.name,
            GACreateCategory.events.navigationCreateButton,
            GACreateCategory.labels.navigationCreateButton,
            1,
        );
        setCreateVisibilityStatus(!createVisibilityStatus);
    };

    const openSubscriptionInfoModal = useCallback(() => {
        setIsSubscriptionInfoModalOpen(true);
    }, []);

    const closeSubscriptionInfoModal = useCallback(() => {
        setIsSubscriptionInfoModalOpen(false);
    }, []);

    const redirectToPredefined = () => {
        logEvent(GAPredefinedCategory.name, GAPredefinedCategory.events.navigateList, l.PredefinedMarketingText);
        goToRoute(PREDEFINED_LIST);
    };

    return (
        <div className="crm-header">
            <Helmet>
                <body
                    className={classNames(
                        ViewModeTypes[applicationModes.ViewMode],
                        ViewThemeTypes[applicationModes.Appearance] || 'view-theme-light',
                    )}
                />
            </Helmet>
            <div className="crm-header-column">
                <Link to={getRoutePath(HOMEPAGE)}>
                    <Image
                        src="/crm-logo-dark.svg"
                        className="logo"
                        isValid={true}
                        selectMode={false}
                        withBorder={false}
                    />
                </Link>

                <Divider type="vertical" withSpace={true} />

                <div className="navigation-wrap" ref={navigationRef}>
                    <Popover
                        isOpen={navigationVisibilityStatus}
                        extendTargetWidth={false}
                        className="navigation-popover-content"
                        maxHeight={352}
                        height={352}
                        scrollbarNeeded={false}
                    >
                        <>
                            <div className={'navigation-button-wrap'}>
                                <Button
                                    onClick={() => setNavigationVisibilityStatus(!navigationVisibilityStatus)}
                                    flexibility="content-size"
                                    className="h-btn-dash"
                                    icon="bc-icon-arrow-down"
                                >
                                    {t(activeNavigationName)}
                                </Button>
                            </div>
                            {navigationVisibilityStatus && (
                                <Navigation
                                    setNavigationVisibilityStatus={setNavigationVisibilityStatus}
                                    navigationList={NavigationList}
                                />
                            )}
                        </>
                    </Popover>
                </div>

                <Divider type="vertical" withSpace={true} />

                <GlobalSearch />
            </div>
            <div className="crm-header-column">
                {(hasUpcomingMaintenance || (isUnderMaintenance && hasPermission([PermissionEnum.CRMAdmin]))) && (
                    <>
                        <MaintenanceInfo
                            startDate={startDate}
                            endDate={endDate}
                            isUnderMaintenance={isUnderMaintenance}
                        />
                        <Divider type="vertical" withSpace={true} />
                    </>
                )}

                <BlockedBySubscriptionInfo isBlocked={!isPredefinedAvailable} position="bottom">
                    <div>
                        <Button
                            className={styles.predefinedButton}
                            cornerRadius="smooth"
                            onClick={redirectToPredefined}
                            disabled={!isPredefinedAvailable}
                        >
                            <ScrollingText>{t(l.PredefinedMarketingText)}</ScrollingText>
                        </Button>
                    </div>
                </BlockedBySubscriptionInfo>

                <Divider type="vertical" withSpace={true} />

                <Popover
                    className="create-menu-popover"
                    isOpen={createVisibilityStatus}
                    extendTargetWidth={false}
                    minHeight={277}
                    align="center"
                    scrollbarNeeded={false}
                >
                    <div className="create-wrap" ref={createRef}>
                        <Button
                            className="create-btn"
                            icon={createVisibilityStatus ? 'bc-icon-clear' : 'bc-icon-plus-crm'}
                            appearance="outline"
                            active={createVisibilityStatus}
                            onClick={() => handleCreate()}
                        >
                            {t(l.Create)}
                        </Button>
                        {createVisibilityStatus && <Create setCreateVisibilityStatus={setCreateVisibilityStatus} />}
                    </div>
                </Popover>

                <Divider type="vertical" withSpace={true} />

                <Partners
                    setIsLoading={setIsLoading}
                    setPartnersVisibilityStatus={setPartnersVisibilityStatus}
                    isVisible={partnersVisibilityStatus}
                    toggleHandler={togglePartnersVisibility}
                />
                <Divider type="vertical" withSpace={true} />

                <ButtonWithLoader
                    defaultClassNames="partner-subscription-button"
                    isLoading={isLoading || isNil(DisplayName)}
                    onClick={openSubscriptionInfoModal}
                >
                    {DisplayName}
                </ButtonWithLoader>
                <Divider type="vertical" withSpace={true} />

                <SkeletonLoader isBusy={isLoading} height={'20px'} width={'55px'} circle>
                    <div className="time-holder">{!isLoading && <UserTime />}</div>
                </SkeletonLoader>

                <Divider type="vertical" withSpace={true} />
                {isNotificationsAvailable ? (
                    <NotificationPanel />
                ) : (
                    <BlockedBySubscriptionInfo isBlocked={true} position="bottom">
                        <div>
                            <Icon type="bc-icon-bell-outline" className="notification-icon" />
                        </div>
                    </BlockedBySubscriptionInfo>
                )}
                <Divider type="vertical" withSpace={true} />

                <div className="profile-wrap" ref={userProfileRef}>
                    <Popover
                        isOpen={userProfileVisibilityStatus}
                        extendTargetWidth={false}
                        minHeight={277}
                        align="end"
                        scrollbarNeeded={false}
                    >
                        <>
                            <Button
                                onClick={() => setUserProfileVisibilityStatus(!userProfileVisibilityStatus)}
                                className="user-btn"
                                icon="bc-icon-user"
                            >
                                {FirstName}
                            </Button>
                            {userProfileVisibilityStatus && (
                                <UserProfile
                                    settingsModalVisibleState={isVisibleUserProfileSettingsModal}
                                    setSettingsModalVisibleState={setIsVisibleUserProfileSettingsModal}
                                    isVisibleProfileModal={isVisibleProfileModal}
                                    setIsVisibleProfileModal={setIsVisibleProfileModal}
                                    signOutHandler={signOutHandler}
                                    saveSettings={saveSettings}
                                    changeLanguageHandler={changeLanguageHandler}
                                />
                            )}
                        </>
                    </Popover>
                </div>
            </div>

            {isSubscriptionInfoModalOpen && <SubscriptionInfoModal handleClose={closeSubscriptionInfoModal} />}
        </div>
    );
};

export default memo(Header);
